import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  Mic,
  Send,
  CheckCircle2,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Search,
  Trophy,
  Headphones,
} from 'lucide-react';
import { getLeaderboards } from '../../services/annotationApi';
import { formatHm } from '../../utils/formatDuration';
import { UserEmail } from '../../components/common/UserEmail';
import { roleColorClasses, roleLabel } from '../../utils/roleStyles';
import { useCurrentUser } from '../../context/UserContext';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface UserRecord {
  userId: string;
  name?: string;
  email?: string;
  role?: string;
  phone?: string;
}

type PresetKey = '24h' | '7d' | '30d' | 'all';
type DateMode = 'preset' | 'custom';
interface DateFilter {
  mode: DateMode;
  presetKey: PresetKey;
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
}

type SortDir = 'asc' | 'desc';
type UploadSortKey = 'rank' | 'email' | 'totalSessions' | 'recordingHours';
type TranscribeSortKey =
  | 'rank'
  | 'email'
  | 'submittedSessions'
  | 'transcribedHours'
  | 'approvedSessions'
  | 'approvedHours';

// Text columns naturally read A→Z on first click; numeric columns naturally
// read highest-first. Rank sorts by the primary metric, so its "desc" =
// highest score = rank 1 first.
function defaultDirForUploadKey(k: UploadSortKey): SortDir {
  return k === 'email' ? 'asc' : 'desc';
}
function defaultDirForTranscribeKey(k: TranscribeSortKey): SortDir {
  return k === 'email' ? 'asc' : 'desc';
}

// Sort/filter key for a user — we identify users by email throughout the
// leaderboards, with a name fallback so users without an email still sort.
function userKey(u: UserRecord): string {
  return (u.email ?? u.name ?? '').toLowerCase();
}

// Shared identity cell used by both leaderboards. Stacks display name on
// top of the role-coloured email pill. Phone lives in its own column so
// it stays scannable as a distinct piece of info.
function UserCell({ user }: { user: UserRecord }) {
  // Hide the name line when it duplicates the email — older signups stored
  // displayName as the user's email, and rendering both would look like
  // the email is shown twice.
  const showName = user.name && user.name.toLowerCase() !== (user.email ?? '').toLowerCase();
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {showName && (
        <div className="text-sm font-medium text-gray-900 truncate">
          {user.name}
        </div>
      )}
      {user.email ? (
        <UserEmail email={user.email} role={user.role} />
      ) : (
        !showName && (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${roleColorClasses(user.role)}`}
          >
            {user.name ?? roleLabel(user.role)}
          </span>
        )
      )}
    </div>
  );
}

interface UploadRow {
  userId: string;
  user: UserRecord;
  totalSessions: number;
  recordingSeconds: number;
}

interface TranscribeRow {
  userId: string;
  user: UserRecord;
  submittedSessions: number;
  transcribedSeconds: number;
  approvedSessions: number;
  approvedSeconds: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────


function presetRange(key: PresetKey): { from?: Date; to?: Date } {
  if (key === 'all') return {};
  const now = new Date();
  const from = new Date(now);
  if (key === '24h') from.setHours(now.getHours() - 24);
  else if (key === '7d') from.setDate(now.getDate() - 7);
  else if (key === '30d') from.setDate(now.getDate() - 30);
  return { from, to: now };
}

function customRange(filter: DateFilter): { from?: Date; to?: Date } {
  const from = filter.from ? new Date(`${filter.from}T00:00:00`) : undefined;
  const to = filter.to ? new Date(`${filter.to}T23:59:59.999`) : undefined;
  return { from, to };
}

function activeRange(filter: DateFilter) {
  return filter.mode === 'preset' ? presetRange(filter.presetKey) : customRange(filter);
}

function presetLabel(key: PresetKey): string {
  return key === '24h' ? 'Last 24h' : key === '7d' ? 'Last 7d' : key === '30d' ? 'Last 30d' : 'All time';
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  colorClass: string;
}

function StatCard({ label, value, icon, colorClass }: StatCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClass}`}>
          {icon}
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function Skeleton({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-200 rounded-xl ${className ?? ''}`} />;
}

// ─── Sort header ───────────────────────────────────────────────────────────────

function SortHeader<K extends string>({
  label,
  sortKey,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: K;
  current: K;
  dir: SortDir;
  onSort: (key: K) => void;
  align?: 'left' | 'right';
}) {
  const active = current === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === 'desc' ? ChevronDown : ChevronUp;
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => onSort(sortKey)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <Icon size={12} className={active ? 'text-violet-600' : 'text-gray-400'} />
      </span>
    </th>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TimeboundAnalyticsPage() {
  const { role } = useCurrentUser();

  const [dateFilter, setDateFilter] = useState<DateFilter>({ mode: 'preset', presetKey: 'all' });
  const [userQuery, setUserQuery] = useState('');
  const [uploadSort, setUploadSort] = useState<{ key: UploadSortKey; dir: SortDir }>({
    key: 'totalSessions',
    dir: 'desc',
  });
  const [transcribeSort, setTranscribeSort] = useState<{ key: TranscribeSortKey; dir: SortDir }>({
    key: 'submittedSessions',
    dir: 'desc',
  });

  const allowed = role === 'admin' || role === 'supervisor' || role === 'sub-admin';

  // ── Data fetch ─────────────────────────────────────────────────────────────
  // One server-side aggregated endpoint replaces the prior three-fetch flow
  // (sessions + tasks + users). Returns ≤200 rows per leaderboard with name
  // / email / role / phone pre-attached and rejected sessions already excluded.
  //
  // Date filter shape: leaderboards endpoint expects ISO date strings
  // (YYYY-MM-DD), inclusive on both ends. Convert from the page's
  // DateFilter (presets or custom).

  const dateParams = useMemo<{ from?: string; to?: string }>(() => {
    const { from, to } = activeRange(dateFilter);
    const fmt = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      from: from ? fmt(from) : undefined,
      to: to ? fmt(to) : undefined,
    };
  }, [dateFilter]);

  const {
    data: leaderboards,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['analytics-leaderboards', dateParams],
    queryFn: () => getLeaderboards(dateParams),
    enabled: allowed,
  });

  // ── Per-user aggregates (server-derived) ─────────────────────────────────

  const summary = useMemo(() => {
    const u = leaderboards?.uploads.summary;
    const t = leaderboards?.transcribes.summary;
    return {
      totalSessions: u?.totalSessions ?? 0,
      recordingSeconds: u?.recordingSeconds ?? 0,
      submittedSessions: t?.submittedSessions ?? 0,
      transcribedSeconds: t?.transcribedSeconds ?? 0,
      approvedSessions: t?.approvedSessions ?? 0,
      approvedSeconds: t?.approvedSeconds ?? 0,
    };
  }, [leaderboards]);

  const uploadRows: UploadRow[] = useMemo(() => {
    return (leaderboards?.uploads.rows ?? []).map((r) => ({
      userId: r.userId,
      user: {
        userId: r.userId,
        name: r.name,
        email: r.email,
        role: r.role,
        phone: r.phone,
      },
      totalSessions: r.totalSessions,
      recordingSeconds: r.recordingSeconds,
    }));
  }, [leaderboards]);

  const transcribeRows: TranscribeRow[] = useMemo(() => {
    return (leaderboards?.transcribes.rows ?? []).map((r) => ({
      userId: r.userId,
      user: {
        userId: r.userId,
        name: r.name,
        email: r.email,
        role: r.role,
        phone: r.phone,
      },
      submittedSessions: r.submittedSessions,
      transcribedSeconds: r.transcribedSeconds,
      approvedSessions: r.approvedSessions,
      approvedSeconds: r.approvedSeconds,
    }));
  }, [leaderboards]);

  // ── User filter (narrows leaderboards, not summary) ───────────────────────

  const filterRows = <R extends { user: UserRecord }>(rows: R[]): R[] => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const u = r.user;
      return (
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.name && u.name.toLowerCase().includes(q))
      );
    });
  };

  // ── Sort ──────────────────────────────────────────────────────────────────

  const sortedUpload = useMemo(() => {
    const rows = filterRows(uploadRows);
    const { key, dir } = uploadSort;
    const factor = dir === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (key === 'email') {
        cmp = userKey(a.user).localeCompare(userKey(b.user));
      } else if (key === 'totalSessions' || key === 'rank') {
        cmp = a.totalSessions - b.totalSessions;
      } else if (key === 'recordingHours') {
        cmp = a.recordingSeconds - b.recordingSeconds;
      }
      if (cmp === 0) cmp = userKey(a.user).localeCompare(userKey(b.user));
      return cmp * factor;
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadRows, userQuery, uploadSort]);

  const sortedTranscribe = useMemo(() => {
    const rows = filterRows(transcribeRows);
    const { key, dir } = transcribeSort;
    const factor = dir === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (key === 'email') {
        cmp = userKey(a.user).localeCompare(userKey(b.user));
      } else if (key === 'submittedSessions' || key === 'rank') {
        cmp = a.submittedSessions - b.submittedSessions;
      } else if (key === 'transcribedHours') {
        cmp = a.transcribedSeconds - b.transcribedSeconds;
      } else if (key === 'approvedSessions') {
        cmp = a.approvedSessions - b.approvedSessions;
      } else if (key === 'approvedHours') {
        cmp = a.approvedSeconds - b.approvedSeconds;
      }
      if (cmp === 0) cmp = userKey(a.user).localeCompare(userKey(b.user));
      return cmp * factor;
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribeRows, userQuery, transcribeSort]);

  // ── Sort click handlers (toggle direction on second click) ────────────────

  const toggleUploadSort = (key: UploadSortKey) =>
    setUploadSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: defaultDirForUploadKey(key) },
    );

  const toggleTranscribeSort = (key: TranscribeSortKey) =>
    setTranscribeSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: defaultDirForTranscribeKey(key) },
    );

  // ── Preset / custom date input handlers ───────────────────────────────────

  const setPreset = (presetKey: PresetKey) =>
    setDateFilter({ mode: 'preset', presetKey, from: undefined, to: undefined });

  const setCustomFrom = (from: string) =>
    setDateFilter((prev) => ({ mode: 'custom', presetKey: 'all', from, to: prev.to }));

  const setCustomTo = (to: string) =>
    setDateFilter((prev) => ({ mode: 'custom', presetKey: 'all', from: prev.from, to }));

  // ── Render ────────────────────────────────────────────────────────────────

  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <p className="text-gray-600 font-medium">You do not have access to this page.</p>
      </div>
    );
  }

  const loading = isLoading;
  const hasError = isError;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timebound Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Activity within a time window, with upload &amp; transcription leaderboards
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {(['24h', '7d', '30d', 'all'] as PresetKey[]).map((k) => {
            const active = dateFilter.mode === 'preset' && dateFilter.presetKey === k;
            return (
              <button
                key={k}
                onClick={() => setPreset(k)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  active
                    ? 'bg-violet-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {presetLabel(k)}
              </button>
            );
          })}
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
            <input
              type="date"
              value={dateFilter.mode === 'custom' ? dateFilter.from ?? '' : ''}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={dateFilter.mode === 'custom' ? dateFilter.to ?? '' : ''}
              onChange={(e) => setCustomTo(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        <div className="relative w-full lg:w-80">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            placeholder="Filter leaderboards by email or name"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      </div>

      {hasError && (
        <div className="flex flex-col items-center justify-center h-32 gap-2 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <p className="text-red-700 font-medium">Failed to load data.</p>
        </div>
      )}

      {/* Summary cards */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            label="Total Sessions"
            value={summary.totalSessions}
            icon={<Mic className="w-4 h-4 text-violet-600" />}
            colorClass="bg-violet-100"
          />
          <StatCard
            label="Recording Hours"
            value={formatHm(summary.recordingSeconds)}
            icon={<Mic className="w-4 h-4 text-violet-600" />}
            colorClass="bg-violet-100"
          />
          <StatCard
            label="Submitted Sessions"
            value={summary.submittedSessions}
            icon={<Send className="w-4 h-4 text-yellow-600" />}
            colorClass="bg-yellow-100"
          />
          <StatCard
            label="Transcribed Hours"
            value={formatHm(summary.transcribedSeconds)}
            icon={<Headphones className="w-4 h-4 text-yellow-600" />}
            colorClass="bg-yellow-100"
          />
          <StatCard
            label="Approved Sessions"
            value={summary.approvedSessions}
            icon={<CheckCircle2 className="w-4 h-4 text-green-600" />}
            colorClass="bg-green-100"
          />
          <StatCard
            label="Approved Hours"
            value={formatHm(summary.approvedSeconds)}
            icon={<CheckCircle2 className="w-4 h-4 text-green-600" />}
            colorClass="bg-green-100"
          />
        </div>
      )}

      {/* Upload leaderboard */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Trophy size={16} className="text-violet-600" />
          <h2 className="text-sm font-semibold text-gray-700">Upload Leaderboard</h2>
          <span className="text-xs text-gray-400 font-normal ml-1">
            sessions recorded in selected window
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <SortHeader
                  label="Rank"
                  sortKey={'rank' as UploadSortKey}
                  current={uploadSort.key}
                  dir={uploadSort.dir}
                  onSort={toggleUploadSort}
                />
                <SortHeader
                  label="User"
                  sortKey={'email' as UploadSortKey}
                  current={uploadSort.key}
                  dir={uploadSort.dir}
                  onSort={toggleUploadSort}
                />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Phone
                </th>
                <SortHeader
                  label="Total Sessions"
                  sortKey={'totalSessions' as UploadSortKey}
                  current={uploadSort.key}
                  dir={uploadSort.dir}
                  onSort={toggleUploadSort}
                  align="right"
                />
                <SortHeader
                  label="Recording Hours"
                  sortKey={'recordingHours' as UploadSortKey}
                  current={uploadSort.key}
                  dir={uploadSort.dir}
                  onSort={toggleUploadSort}
                  align="right"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedUpload.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No results in this range.
                  </td>
                </tr>
              ) : (
                sortedUpload.map((r, i) => (
                  <tr key={r.userId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 font-mono w-12">{i + 1}</td>
                    <td className="px-4 py-3">
                      <UserCell user={r.user} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono whitespace-nowrap">
                      {r.user.phone ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-medium">
                      {r.totalSessions}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatHm(r.recordingSeconds)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transcribe leaderboard */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Trophy size={16} className="text-yellow-600" />
          <h2 className="text-sm font-semibold text-gray-700">Transcribe Leaderboard</h2>
          <span className="text-xs text-gray-400 font-normal ml-1">
            tasks submitted / approved in selected window
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <SortHeader
                  label="Rank"
                  sortKey={'rank' as TranscribeSortKey}
                  current={transcribeSort.key}
                  dir={transcribeSort.dir}
                  onSort={toggleTranscribeSort}
                />
                <SortHeader
                  label="User"
                  sortKey={'email' as TranscribeSortKey}
                  current={transcribeSort.key}
                  dir={transcribeSort.dir}
                  onSort={toggleTranscribeSort}
                />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Phone
                </th>
                <SortHeader
                  label="Submitted Sessions"
                  sortKey={'submittedSessions' as TranscribeSortKey}
                  current={transcribeSort.key}
                  dir={transcribeSort.dir}
                  onSort={toggleTranscribeSort}
                  align="right"
                />
                <SortHeader
                  label="Transcribed Hours"
                  sortKey={'transcribedHours' as TranscribeSortKey}
                  current={transcribeSort.key}
                  dir={transcribeSort.dir}
                  onSort={toggleTranscribeSort}
                  align="right"
                />
                <SortHeader
                  label="Approved Sessions"
                  sortKey={'approvedSessions' as TranscribeSortKey}
                  current={transcribeSort.key}
                  dir={transcribeSort.dir}
                  onSort={toggleTranscribeSort}
                  align="right"
                />
                <SortHeader
                  label="Approved Hours"
                  sortKey={'approvedHours' as TranscribeSortKey}
                  current={transcribeSort.key}
                  dir={transcribeSort.dir}
                  onSort={toggleTranscribeSort}
                  align="right"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedTranscribe.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No results in this range.
                  </td>
                </tr>
              ) : (
                sortedTranscribe.map((r, i) => (
                  <tr key={r.userId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 font-mono w-12">{i + 1}</td>
                    <td className="px-4 py-3">
                      <UserCell user={r.user} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono whitespace-nowrap">
                      {r.user.phone ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-medium">
                      {r.submittedSessions}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatHm(r.transcribedSeconds)}
                    </td>
                    <td className="px-4 py-3 text-right text-green-700 font-medium">
                      {r.approvedSessions}
                    </td>
                    <td className="px-4 py-3 text-right text-green-700">
                      {formatHm(r.approvedSeconds)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
